import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useI18n } from "@/i18n";
import { Clock } from "lucide-react";

// 12 地支时辰 with hour ranges. 'zh' name + english label.
const SHICHEN = [
  { zh: "子", en: "Zi", start: 23 }, { zh: "丑", en: "Chou", start: 1 },
  { zh: "寅", en: "Yin", start: 3 }, { zh: "卯", en: "Mao", start: 5 },
  { zh: "辰", en: "Chen", start: 7 }, { zh: "巳", en: "Si", start: 9 },
  { zh: "午", en: "Wu", start: 11 }, { zh: "未", en: "Wei", start: 13 },
  { zh: "申", en: "Shen", start: 15 }, { zh: "酉", en: "You", start: 17 },
  { zh: "戌", en: "Xu", start: 19 }, { zh: "亥", en: "Hai", start: 21 },
];

function currentIndex() {
  const h = new Date().getHours();
  return Math.floor(((h + 1) % 24) / 2);
}

// Fallback: derive a per-时辰 level from the fortune's lucky-hours text
// (only 吉/平, no 凶) when the backend has not supplied structured 吉/平/凶.
function levelsFromLucky(luckyHours) {
  const text = (luckyHours || []).join(" ");
  return SHICHEN.map((s) => {
    const hourStr = String(s.start).padStart(2, "0");
    const good = text.includes(s.zh) || text.includes(`${hourStr}:`) || text.includes(s.en);
    return good ? "ji" : "ping";
  });
}

const LEVEL_STYLE = {
  ji: { bg: "hsl(var(--brand-gold))", text: "text-[hsl(var(--brand-ink))]" },
  ping: { bg: "hsl(var(--secondary))", text: "text-muted-foreground" },
  xiong: { bg: "hsla(var(--brand-vermillion),0.18)", text: "text-[hsl(var(--brand-vermillion))]" },
};

export const ShichenDial = ({ shichen, luckyHours }) => {
  const { L } = useI18n();
  // Prefer real backend 吉/平/凶; otherwise fall back to lucky-hours heuristic.
  const hasReal = Array.isArray(shichen) && shichen.length === 12;
  const levels = hasReal ? shichen.map((s) => s.level) : levelsFromLucky(luckyHours);
  const ranges = hasReal ? shichen.map((s) => s.range) : SHICHEN.map(() => "");
  const cur = currentIndex();
  const size = 232, r = 92, cx = size / 2, cy = size / 2;
  const curName = SHICHEN[cur].zh;
  const curLevel = levels[cur] || "ping";

  const levelLabel = (lv) => (lv === "ji" ? L("吉", "Lucky") : lv === "xiong" ? L("凶", "Caution") : L("平", "Neutral"));
  const curLevelColor = curLevel === "ji"
    ? "text-[hsl(var(--brand-bronze))]"
    : curLevel === "xiong" ? "text-[hsl(var(--brand-vermillion))]" : "text-muted-foreground";

  return (
    <Card className="border-border bg-card" data-testid="shichen-dial">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[hsla(var(--brand-gold-soft),0.5)]">
            <Clock className="h-3.5 w-3.5 text-[hsl(var(--brand-bronze))]" />
          </span>
          <span className="text-sm font-bold text-foreground">{L("今日时辰吉凶", "Hourly Luck (12 时辰)")}</span>
        </div>
        <div className="flex justify-center">
          <div className="relative" style={{ width: size, height: size }}>
            <div className="absolute inset-0 rounded-full border border-border" />
            {SHICHEN.map((s, i) => {
              const angle = (i / 12) * 2 * Math.PI - Math.PI / 2;
              const x = cx + r * Math.cos(angle);
              const y = cy + r * Math.sin(angle);
              const lv = levels[i] || "ping";
              const st = LEVEL_STYLE[lv];
              const isCur = i === cur;
              return (
                <div key={i}
                  data-testid={`shichen-${s.en.toLowerCase()}`}
                  data-level={lv}
                  title={ranges[i] ? `${s.zh}时 ${ranges[i]} · ${levelLabel(lv)}` : undefined}
                  className={`absolute flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-sm font-bold transition-transform ${st.text} ${isCur ? "scale-125 ring-2 ring-[hsl(var(--brand-vermillion))]" : ""}`}
                  style={{ left: x, top: y, background: st.bg }}
                >
                  {s.zh}
                </div>
              );
            })}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <div className="text-[11px] text-muted-foreground">{L("当前时辰", "Now")}</div>
              <div className="font-display text-2xl font-bold text-[hsl(var(--brand-vermillion))]">{curName}{L("时", "")}</div>
              <div className={`text-xs font-semibold ${curLevelColor}`}>{levelLabel(curLevel)}</div>
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full" style={{ background: "hsl(var(--brand-gold))" }} />{L("吉", "Lucky")}</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full" style={{ background: "hsl(var(--secondary))" }} />{L("平", "Neutral")}</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-full" style={{ background: "hsla(var(--brand-vermillion),0.35)" }} />{L("凶", "Caution")}</span>
        </div>
      </CardContent>
    </Card>
  );
};
